<template>
  <section class="code-contact-page overflow-x-hidden bg-neutral-50">
    <div class="mx-auto w-full min-w-0 max-w-[1200px] px-4 py-8 sm:px-6 lg:px-0">
      <nav class="mb-6 flex min-w-0 flex-wrap items-center gap-1 text-sm" aria-label="Breadcrumb">
        <Link :href="homeHref" class="shrink-0 font-medium text-neutral-700 hover:text-[#2ea8cf]">
          {{ breadcrumbHomeLabel }}
        </Link>
        <span class="shrink-0 text-neutral-300" aria-hidden="true">/</span>
        <span class="font-medium text-[#2ea8cf]">{{ breadcrumbCurrentLabel }}</span>
      </nav>

      <div class="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start lg:gap-8">
        <!-- Left: address + visuals -->
        <div class="flex min-w-0 flex-col gap-6 lg:gap-8">
          <div
            class="flex min-w-0 flex-col gap-4 rounded-xl bg-white p-4 shadow-[0px_6px_15px_-2px_rgba(16,24,40,0.08)] lg:gap-5 lg:p-8"
          >
            <h1 class="break-words text-[32px] font-medium leading-10 tracking-[-0.02em] lg:text-[40px] lg:leading-[48px]">
              {{ addressCardHeading }}
            </h1>
            <div class="flex flex-col items-start gap-3">
              <div class="break-words text-base leading-7 text-neutral-900 lg:text-lg">
                <p>
                  <span class="font-semibold">{{ businessName }}</span>
                  <br>
                  {{ addressLine1 }}
                </p>
                <p>{{ addressLine2 }}</p>
              </div>
              <button
                type="button"
                class="w-fit cursor-pointer self-start text-left pl-0"
                :aria-haspopup="'dialog'"
                :aria-expanded="parkingModalOpen"
                @click="parkingModalOpen = true"
              >
                <ContactParkingRow>
                  <span class="text-left text-base font-semibold leading-[18px] text-neutral-900 underline decoration-solid underline-offset-[3px] [text-underline-position:from-font]">
                    {{ parkingLinkText }}
                  </span>
                </ContactParkingRow>
              </button>
            </div>
            <ContactPhoneRow>
              <p class="text-base leading-[18px] text-neutral-900">
                <a
                  :href="`tel:${phoneNumber}`"
                  class="mr-1 font-semibold text-neutral-900 no-underline"
                >{{ phoneNumber }}</a><span class="text-neutral-500">{{ phoneHours }}</span>
              </p>
            </ContactPhoneRow>
            <ContactEmailRow>
              <a
                :href="`mailto:${emailAddress}`"
                class="break-all text-base font-semibold leading-[18px] text-neutral-900 no-underline"
              >{{ emailAddress }}</a>
            </ContactEmailRow>
          </div>

          <div
            class="relative aspect-[16/10] w-full overflow-hidden rounded-xl border border-neutral-200 transition-opacity duration-600 ease-out lg:aspect-auto lg:h-[386px]"
            :style="{ opacity: mapLoaded ? 1 : 0.5 }"
          >
            <iframe
              :src="mapEmbedUrl"
              :title="mapIframeTitle"
              class="absolute inset-0 h-full w-full border-0"
              loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"
              allowfullscreen
              @load="mapLoaded = true"
            />
          </div>
          <img
            v-if="locationPhoto"
            :src="locationPhoto"
            :alt="locationPhotoAlt"
            class="aspect-[16/10] w-full rounded-xl object-cover lg:aspect-auto lg:h-[386px]"
          >
        </div>

        <form
          class="relative flex min-w-0 flex-col gap-4 rounded-xl bg-white p-4 shadow-[0px_6px_15px_-2px_rgba(16,24,40,0.08)] lg:gap-6 lg:p-8"
          novalidate
          @submit.prevent="submit"
        >
          <h2 class="text-xl font-medium leading-7 tracking-[-0.02em]">
            {{ formTitle }}
          </h2>

          <div
            v-if="successMessage"
            class="rounded-lg bg-green-50 p-4 text-sm text-green-800"
            role="status"
          >
            {{ successMessage }}
          </div>

          <div
            v-if="errorMessages.length"
            ref="errorAlert"
            class="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm leading-5 text-red-700"
            role="alert"
            tabindex="-1"
          >
            <svg class="mt-0.5 size-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M10.29 3.86 1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <ul v-if="errorMessages.length > 1" class="list-disc space-y-1 pl-4">
              <li v-for="(message, index) in errorMessages" :key="index">{{ message }}</li>
            </ul>
            <span v-else>{{ errorMessages[0] }}</span>
          </div>

          <div class="flex flex-col gap-6">
            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-neutral-900" for="contact-name">{{ nameFieldLabel }}</label>
              <input
                id="contact-name"
                v-model="form.name"
                type="text"
                autocomplete="name"
                :class="[
                  'h-10 w-full rounded-lg border bg-white px-3 text-sm shadow-sm focus:outline-none focus:ring-1',
                  fieldErrorClass('name'),
                ]"
                :aria-invalid="!!errors.name"
                @input="clearFieldError('name')"
              >
            </div>
            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-neutral-900" for="contact-email">{{ emailFieldLabel }}</label>
              <input
                id="contact-email"
                v-model="form.email"
                type="email"
                autocomplete="email"
                :class="[
                  'h-10 w-full rounded-lg border bg-white px-3 text-sm shadow-sm focus:outline-none focus:ring-1',
                  fieldErrorClass('email'),
                ]"
                :aria-invalid="!!errors.email"
                @input="clearFieldError('email')"
              >
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-neutral-900" for="contact-message">{{ messageFieldLabel }}</label>
              <div class="relative">
                <textarea
                  id="contact-message"
                  v-model="form.message"
                  rows="5"
                  :maxlength="MESSAGE_MAX"
                  :placeholder="messagePlaceholder"
                  :class="[
                    'w-full resize-none rounded-lg border bg-white px-3 py-2 text-sm text-neutral-900 shadow-sm focus:outline-none focus:ring-1',
                    fieldErrorClass('message'),
                  ]"
                  :aria-invalid="!!errors.message"
                  @input="clearFieldError('message')"
                />
                <span class="pointer-events-none absolute bottom-2 right-3 text-sm text-neutral-500">
                  {{ form.message.length }}/{{ MESSAGE_MAX }}
                </span>
              </div>
            </div>
          </div>

          <div class="flex flex-col gap-1">
            <label class="flex cursor-pointer items-start gap-3 py-1 lg:px-5 lg:py-3">
              <input
                v-model="form.consent"
                type="checkbox"
                class="mt-1 size-[18px] shrink-0 rounded border-neutral-300 text-code-medical-center focus:ring-code-medical-center"
                @change="clearFieldError('consent')"
              >
              <span class="break-words text-base font-medium leading-6 text-neutral-900">{{ consentText }}</span>
            </label>
          </div>

          <button
            type="submit"
            :disabled="!canSubmit || processing"
            class="flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg px-5 py-3.5 text-base font-semibold leading-[18px] text-white drop-shadow-[0px_2px_3px_rgba(16,24,40,0.06)] disabled:cursor-not-allowed disabled:opacity-50"
            style="background-image: linear-gradient(176.294deg, rgb(46, 168, 207) 0%, rgb(40, 182, 155) 100%)"
          >
            <Icon
              v-if="!processing"
              name="check"
              class="size-5 shrink-0"
            />
            {{ processing ? sendingLabel : submitLabel }}
          </button>
        </form>
      </div>
    </div>

    <ParkingModal :open="parkingModalOpen" @close="parkingModalOpen = false" />
  </section>
</template>

<script setup>
import { computed, nextTick, reactive, ref } from 'vue';
import { Link, usePage } from '@inertiajs/vue3';
import axios from 'axios';
import ContactPhoneRow from '@/Shared/ContactPhoneRow.vue';
import ContactEmailRow from '@/Shared/ContactEmailRow.vue';
import ContactParkingRow from '@/Shared/ContactParkingRow.vue';
import ParkingModal from '@/Shared/ParkingModal.vue';
import Icon from '@/Shared/Icon.vue';
import { useTranslations } from '@/utils/translations.js';

const props = defineProps({
  breadcrumbHomeLabel:    { type: String, default: 'Strona główna' },
  breadcrumbCurrentLabel: { type: String, default: 'Kontakt' },
  addressCardHeading:     { type: String, default: 'Adres i kontakt' },
  businessName:           { type: String, default: 'Centrum Medyczne CODE' },
  addressLine1:           { type: String, default: 'ul. Juliana Tuwima 15 lok. U4' },
  addressLine2:           { type: String, default: '90-010 Łódź' },
  parkingLinkLabel:       { type: String, default: '' },
  phoneNumber:            { type: String, default: '887 220 088' },
  phoneHours:             { type: String, default: '(pon-pt: 8:00 - 20:00)' },
  emailAddress:           { type: String, default: 'info@cmscode.pl' },
  formTitle:              { type: String, default: 'Formularz kontaktowy' },
  nameFieldLabel:         { type: String, default: 'Imię i nazwisko *' },
  emailFieldLabel:        { type: String, default: 'Adres e-mail *' },
  messageFieldLabel:      { type: String, default: 'Treść wiadomości *' },
  messagePlaceholder:     { type: String, default: 'Wprowadź treść...' },
  consentText:            { type: String, default: 'Wyrażam zgodę na przetwarzanie moich danych kontaktowych w celu kontaktu ze mną.' },
  submitLabel:            { type: String, default: 'Wyślij wiadomość' },
  sendingLabel:           { type: String, default: 'Wysyłanie...' },
  successMessageText:     { type: String, default: 'Dziękujemy! Twoja wiadomość została wysłana.' },
  locationPhoto:          { type: String, default: '' },
  // eslint-disable-next-line vue/prop-name-casing
  site_context:           { type: String, default: null },
});

const page = usePage();
const { t } = useTranslations();

const MESSAGE_MAX = 240;
const inputOkClass = 'border-neutral-200 focus:border-code-medical-center focus:ring-code-medical-center';
const inputErrorClass = 'border-red-500 focus:border-red-500 focus:ring-red-500';

const parkingModalOpen = ref(false);

const form = reactive({ name: '', email: '', message: '', consent: false });
const errors = reactive({ name: '', email: '', message: '', consent: '' });
const formError = ref('');
const formStartedAt = ref(Date.now());
const processing = ref(false);
const successMessage = ref('');
const errorAlert = ref(null);

const canSubmit = computed(() => form.message.trim().length > 0 && form.consent);

const errorMessages = computed(() => {
  const messages = [];
  if (errors.name) messages.push(errors.name);
  if (errors.email) messages.push(errors.email);
  if (errors.message) messages.push(errors.message);
  if (errors.consent) messages.push(errors.consent);
  if (formError.value) messages.push(formError.value);
  return messages;
});

function fieldErrorClass(field) {
  return errors[field] ? inputErrorClass : inputOkClass;
}

function clearFieldError(field) {
  if (errors[field]) errors[field] = '';
  formError.value = '';
}

function clearErrors() {
  errors.name = '';
  errors.email = '';
  errors.message = '';
  errors.consent = '';
  formError.value = '';
}

function scrollToErrors() {
  nextTick(() => {
    const alert = errorAlert.value;
    if (!alert) return;
    alert.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (typeof alert.focus === 'function') {
      alert.focus({ preventScroll: true });
    }
  });
}

async function submit() {
  successMessage.value = '';
  if (!canSubmit.value || processing.value) return;

  clearErrors();
  processing.value = true;
  try {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    const { data } = await axios.post('/api/contact-form', {
      contact: {
        name: form.name,
        email: form.email,
        message: form.message,
        consent: form.consent,
        site_context: props.site_context,
        form_started_at: formStartedAt.value,
      },
    }, { headers: { 'X-CSRF-Token': csrfToken } });

    successMessage.value = data.message || props.successMessageText;
    form.name = '';
    form.email = '';
    form.message = '';
    form.consent = false;
    formStartedAt.value = Date.now();
    clearErrors();
  } catch (err) {
    const serverErrors = err.response?.data?.errors;
    if (serverErrors) {
      Object.keys(serverErrors).forEach((key) => {
        if (key in errors) errors[key] = serverErrors[key];
      });
      scrollToErrors();
    } else {
      formError.value = err.response?.data?.error || t('contact_page.submit_error');
      scrollToErrors();
    }
  } finally {
    processing.value = false;
  }
}

const homeHref = computed(() => {
  if (props.site_context && props.site_context !== 'code_medical_center') {
    const paths = {
      infertility_treatment_clinic: '/klinika-przyczynowego-leczenia-nieplodnosci',
      institute: '/instytut',
      family_clinic: '/klinika-zdrowia-rodziny',
    };
    return paths[props.site_context] || '/';
  }
  return '/';
});

const mapLoaded = ref(false);

const mapQuery = computed(() => (
  [props.businessName, props.addressLine1, props.addressLine2].filter(Boolean).join(', ')
));

// Google Maps embed without an API key (the public `output=embed` endpoint).
const mapEmbedUrl = computed(() => {
  const locale = page.props.current_locale === 'en' ? 'en' : 'pl';
  return `https://www.google.com/maps?q=${encodeURIComponent(mapQuery.value)}&hl=${locale}&z=16&output=embed`;
});

const mapIframeTitle = computed(() => (
  `${props.businessName} — ${props.addressLine1}, ${props.addressLine2}`
));

const locationPhotoAlt = computed(() => (
  `${props.businessName} — ${t('entrance_alt')}`
));

const parkingLinkText = computed(() => (
  props.parkingLinkLabel || t('contact_page.parking_link')
));
</script>
